import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGamepad, faUsers, faClock } from '@fortawesome/free-solid-svg-icons';
import * as discordApi from '../services/discordApi';
import { DiscordMember } from '../services/discordApi';

interface GameActivity {
  gameName: string;
  players: Array<{
    member: DiscordMember;
    details?: string;
    state?: string;
  }>;
  playerCount: number;
}

const GameTracker: React.FC = () => {
  const [games, setGames] = useState<GameActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGameData = async () => {
    try {
      setError(null);
      const response = await discordApi.getOnlineMembers();

      if (response.success && response.data) {
        // Extract game activities (type 0 = Playing)
        const gameMap = new Map<string, GameActivity>();

        response.data.forEach((member) => {
          member.activities?.forEach((activity) => {
            if (activity.type === 0 && activity.name !== 'Custom Status') {
              const gameName = activity.name;

              if (!gameMap.has(gameName)) {
                gameMap.set(gameName, {
                  gameName,
                  players: [],
                  playerCount: 0,
                });
              }

              const game = gameMap.get(gameName)!;
              game.players.push({
                member,
                details: activity.details || undefined,
                state: activity.state || undefined,
              });
              game.playerCount = game.players.length;
            }
          });
        });

        // Sort by player count (most popular first)
        const sortedGames = Array.from(gameMap.values()).sort(
          (a, b) => b.playerCount - a.playerCount
        );

        setGames(sortedGames);
      } else {
        throw new Error(response.error || 'Failed to fetch game data');
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching game data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGameData();
    // Refresh every 5 minutes
    const interval = setInterval(fetchGameData, 300000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#ffd803] p-8 lg:ml-64 lg:mr-80">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-[#00bbf9] border-8 border-black p-8 mb-8 shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
          <h1 className="text-6xl font-black uppercase mb-4 flex items-center gap-4">
            <FontAwesomeIcon icon={faGamepad} />
            Game Tracker
          </h1>
          <p className="text-xl font-bold">
            See what everyone's playing right now!
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white border-8 border-black p-8 text-center shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <p className="text-2xl font-black">Loading games...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-[#f582ae] border-8 border-black p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <p className="text-2xl font-black mb-2">⚠️ Error</p>
            <p className="font-bold">{error}</p>
          </div>
        )}

        {/* No Games State */}
        {!loading && !error && games.length === 0 && (
          <div className="bg-white border-8 border-black p-8 text-center shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <p className="text-2xl font-black">No one is gaming right now 😴</p>
          </div>
        )}

        {/* Games Grid */}
        {!loading && !error && games.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {games.map((game) => (
              <div
                key={game.gameName}
                className="bg-[#b8f2e6] border-8 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all"
              >
                {/* Game Header */}
                <div className="mb-4">
                  <h2 className="text-2xl font-black uppercase mb-2 break-words">
                    {game.gameName}
                  </h2>
                  <div className="flex items-center gap-2 bg-[#9b5de5] border-4 border-black px-3 py-1 w-fit">
                    <FontAwesomeIcon icon={faUsers} className="text-white" />
                    <span className="font-black text-white">
                      {game.playerCount} {game.playerCount === 1 ? 'player' : 'players'}
                    </span>
                  </div>
                </div>

                {/* Players List */}
                <div className="space-y-3">
                  {game.players.map((player) => (
                    <div
                      key={player.member.id}
                      className="bg-white border-4 border-black p-3"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <img
                          src={discordApi.getAvatarUrl(player.member)}
                          alt={player.member.username}
                          className="w-8 h-8 rounded-full border-2 border-black"
                        />
                        <span className="font-black text-sm truncate">
                          {player.member.displayName || player.member.username}
                        </span>
                      </div>

                      {/* Game Details */}
                      {(player.details || player.state) && (
                        <div className="text-xs font-bold ml-11">
                          {player.details && (
                            <p className="truncate">{player.details}</p>
                          )}
                          {player.state && (
                            <p className="truncate text-gray-700">{player.state}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats Summary */}
        {!loading && !error && games.length > 0 && (
          <div className="mt-8 bg-[#9b5de5] border-8 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-white">
              <div className="text-center">
                <FontAwesomeIcon icon={faGamepad} className="text-4xl mb-2" />
                <p className="text-3xl font-black">{games.length}</p>
                <p className="font-bold">Games Active</p>
              </div>
              <div className="text-center">
                <FontAwesomeIcon icon={faUsers} className="text-4xl mb-2" />
                <p className="text-3xl font-black">
                  {games.reduce((sum, game) => sum + game.playerCount, 0)}
                </p>
                <p className="font-bold">Total Players</p>
              </div>
              <div className="text-center">
                <FontAwesomeIcon icon={faClock} className="text-4xl mb-2" />
                <p className="text-xl font-black">LIVE</p>
                <p className="font-bold">Updates every 5 min</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameTracker;
